import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { HelpdeskTicketInterface } from "shared/types/helpdesk";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import type { HelpdeskFilters } from "./useHelpdeskDataLazy";
import { buildHelpdeskTicketParams } from "../utils/helpdeskTicketParams";

const PAGE_SIZE = 50;

/**
 * Manual page-based pagination hook for the helpdesk list view.
 *
 * Calls the unified `GET /helpdesk/tickets/list` endpoint with
 * `status=open,completion` (or user-selected statuses) and
 * `includeEmailCounts=true`.
 *
 * Only active when `viewMode === 'list'`.
 */
export const useHelpdeskListPagination = (
  filters: HelpdeskFilters = {},
  viewMode: "list" | "kanban" = "list",
) => {
  const { session } = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const [tickets, setTickets] = useState<HelpdeskTicketInterface[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  // Convert filters to query-param strings (same logic as useHelpdeskDataLazy)
  const filterParams = useMemo(
    () => buildHelpdeskTicketParams(filters),
    [filters],
  );

  // Stable serialised key so we can detect filter changes cheaply
  const filterKey = useMemo(
    () => JSON.stringify(filterParams),
    [filterParams],
  );

  // Fetch a single page of tickets from the unified endpoint
  const fetchTickets = useCallback(async () => {
    if (!sessionRef.current || viewMode !== "list") return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE),
        sortField: "id",
        sortDirection: "DESC",
        includeEmailCounts: "true",
        ...filterParams,
      });

      // Default to open+completion when user has no explicit status filter
      if (!params.has("status")) {
        params.set("status", "open,completion");
      }

      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/tickets?${params}`,
        { method: "GET" },
        sessionRef.current,
      );

      if (!response.ok)
        throw new Error(`Failed to fetch tickets: ${response.status}`);

      const result = await response.json();
      setTickets(result.data ?? []);
      setTotal(result.totalCount ?? 0);
    } catch (error) {
      console.error("[useHelpdeskListPagination] Error:", error);
      toast.error("Fout bij ophalen van tickets");
    } finally {
      setIsLoading(false);
    }
  }, [page, filterParams, viewMode]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [filterKey]);

  // Fetch when page or filters change (only in list mode)
  useEffect(() => {
    if (viewMode === "list") fetchTickets();
  }, [viewMode, fetchTickets]);

  return {
    listTickets: tickets,
    setListTickets: setTickets,
    listTotal: total,
    listPage: page,
    setListPage: setPage,
    listPageSize: PAGE_SIZE,
    isListLoading: isLoading,
    fetchListTickets: fetchTickets,
  };
};
